import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  Plus,
  Handshake,
  MoreHorizontal,
  Check,
  X,
  Network,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  ConfirmDialog,
  MetricCard,
  KPIGrid,
} from '@/components/common';
import { partnerNetworksApi, PartnerNetwork } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { formatDate } from '@/lib/utils';

export default function PartnerNetworks() {
  const [asOwner, setAsOwner] = useState<PartnerNetwork[]>([]);
  const [asPartner, setAsPartner] = useState<PartnerNetwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [terminateDialog, setTerminateDialog] = useState<{
    open: boolean;
    partnership?: PartnerNetwork;
  }>({ open: false });
  const [terminating, setTerminating] = useState(false);

  const fetchPartnerships = async () => {
    try {
      setLoading(true);
      const data = await partnerNetworksApi.getAll();
      setAsOwner(data.asOwner);
      setAsPartner(data.asPartner);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartnerships();
  }, []);

  const handleAccept = async (partnership: PartnerNetwork) => {
    try {
      await partnerNetworksApi.accept(partnership.id);
      toast.success('Partnership accepted');
      fetchPartnerships();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleTerminate = async () => {
    if (!terminateDialog.partnership) return;

    try {
      setTerminating(true);
      await partnerNetworksApi.delete(terminateDialog.partnership.id);
      toast.success('Partnership terminated');
      setTerminateDialog({ open: false });
      fetchPartnerships();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setTerminating(false);
    }
  };

  const metrics = useMemo(() => {
    const totalOwner = asOwner.length;
    const activeOwner = asOwner.filter((p) => p.status === 'ACTIVE').length;
    const pendingOwner = asOwner.filter((p) => p.status === 'PENDING').length;
    const totalPartner = asPartner.length;
    const activePartner = asPartner.filter((p) => p.status === 'ACTIVE').length;
    const pendingPartner = asPartner.filter((p) => p.status === 'PENDING').length;

    return { totalOwner, activeOwner, pendingOwner, totalPartner, activePartner, pendingPartner };
  }, [asOwner, asPartner]);

  const ownerColumns: Column<PartnerNetwork>[] = [
    {
      key: 'partnerCompany',
      header: 'Partner',
      render: (p) => (
        <div>
          <div className="font-medium">{p.partnerCompany?.displayName}</div>
          <div className="text-xs leading-4 text-muted-foreground">{p.partnerCompany?.name}</div>
        </div>
      ),
    },
    {
      key: 'partnerTypes',
      header: 'Types',
      render: (p) => (
        <div className="flex flex-wrap gap-1">
          {p.partnerTypes.slice(0, 2).map((type) => (
            <Badge key={type} variant="outline" className="text-xs">
              {type}
            </Badge>
          ))}
          {p.partnerTypes.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{p.partnerTypes.length - 2}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (p) => <StatusBadge status={p.status.toLowerCase() as any} />,
    },
    {
      key: 'accessLevel',
      header: 'Access',
      sortable: true,
      render: (p) => (
        <Badge variant="secondary" className="text-xs">
          {p.accessLevel.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'relationshipStartDate',
      header: 'Since',
      sortable: true,
      render: (p) => (p.relationshipStartDate ? formatDate(p.relationshipStartDate) : '-'),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (p) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* Removed: rendered with no onClick. Partner networks have no
                detail route, so there was nothing for it to open. */}
            {p.status !== 'TERMINATED' && (
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => setTerminateDialog({ open: true, partnership: p })}
              >
                <X className="mr-2 h-4 w-4" />
                Terminate
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const partnerColumns: Column<PartnerNetwork>[] = [
    {
      key: 'ownerCompany',
      header: 'Company',
      render: (p) => (
        <div className="min-w-0">
          <div className="truncate font-semibold">{p.ownerCompany?.displayName}</div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {p.ownerCompany?.name}
          </div>
        </div>
      ),
    },
    {
      key: 'partnerTypes',
      header: 'Your Role',
      render: (p) => (
        <div className="flex flex-wrap gap-1">
          {p.partnerTypes.map((type) => (
            <Badge key={type} variant="outline" className="text-xs">
              {type}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (p) => <StatusBadge status={p.status.toLowerCase() as any} />,
    },
    {
      key: 'accessLevel',
      header: 'Access',
      sortable: true,
      render: (p) => (
        <Badge variant="secondary" className="text-xs">
          {p.accessLevel.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (p) =>
        p.status === 'PENDING' ? (
          <div className="flex gap-1">
            <Button
              size="icon"
              aria-label="Approve"
              variant="ghost"
              className="h-8 w-8 text-success-600"
              onClick={() => handleAccept(p)}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              aria-label="Dismiss"
              variant="ghost"
              className="h-8 w-8 text-destructive"
              onClick={() => setTerminateDialog({ open: true, partnership: p })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="More actions" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Removed: rendered with no onClick. Partner networks have no
                  detail route, so there was nothing for it to open. */}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Partner Networks"
          description="Agreements granting another organisation access to shared records, with scope and start date."
          actions={
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Partner
            </Button>
          }
        />
      </motion.div>

      <div>
        <KPIGrid className="mt-6">
          <MetricCard
            label="My Partners"
            value={metrics.totalOwner}
            sentiment="info"
            icon={<Network className="h-5 w-5" />}
          />
          <MetricCard
            label="Active Partnerships"
            value={metrics.activeOwner + metrics.activePartner}
            sentiment="success"
            icon={<CheckCircle2 className="h-5 w-5" />}
          />
          <MetricCard
            label="Pending Requests"
            value={metrics.pendingOwner + metrics.pendingPartner}
            sentiment={metrics.pendingOwner + metrics.pendingPartner > 0 ? 'warning' : 'neutral'}
            icon={<Clock className="h-5 w-5" />}
          />
          <MetricCard
            label="Partner Of"
            value={metrics.totalPartner}
            sentiment="neutral"
            icon={<Handshake className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      <motion.div variants={fadeUp} className="mt-6">
        <Tabs defaultValue="my-partners" className="w-full">
          <TabsList>
            <TabsTrigger value="my-partners">My Partners ({asOwner.length})</TabsTrigger>
            <TabsTrigger value="partner-of">Partner Of ({asPartner.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="my-partners" className="mt-4">
            <DataTable
              data={asOwner}
              columns={ownerColumns}
              loading={loading}
              pagination
              exportable
              columnToggle
              searchable
              emptyTitle="No partners yet"
              emptyDescription="Invite business partners to collaborate."
              emptyAction={
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Partner
                </Button>
              }
            />
          </TabsContent>

          <TabsContent value="partner-of" className="mt-4">
            <DataTable
              data={asPartner}
              columns={partnerColumns}
              loading={loading}
              pagination
              exportable
              columnToggle
              searchable
              emptyTitle="Not a partner of any company"
              emptyDescription="You'll see partnership invitations here."
            />
          </TabsContent>
        </Tabs>
      </motion.div>

      <ConfirmDialog
        open={terminateDialog.open}
        onOpenChange={(open) => setTerminateDialog({ open })}
        title="Terminate Partnership"
        description="Are you sure you want to terminate this partnership? The partner will lose access to shared resources."
        confirmLabel="Terminate"
        onConfirm={handleTerminate}
        variant="destructive"
        loading={terminating}
      />
    </motion.main>
  );
}
